import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import UserIcon from '@/components/icons/UserIcon';
import LogoutIcon from '@/components/icons/LogoutIcon';
import EAvatar from '@/components/everridge/EAvatar';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from '@/features/identity/hooks/useAuthUser.ts';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import NotificationIcon from '@/components/icons/NotificationIcon';
import AccountPopupLanguage from '@/features/account/components/AccountPopupLanguage';
import AccountPopupCurrency from '@/features/account/components/AccountPopupCurrency';
import { useSignOut } from '@/features/identity/hooks/useSignOut.ts';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { ApiConfig } from '@/config/api.config.ts';
import { Roles } from '@/@types/auth.types';
import { cn } from '@/lib/utils';
import ArrowReloadHorizontalIcon from '@/components/icons/ArrowReloadHorizontalIcon';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';
import { useTenantsApi } from '@/features/identity/hooks/useTenantsApi';

const AccountPopup = () => {
    const [isOpen, setIsOpen] = useState(false);

    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const signOut = useSignOut();
    const { user, setTenant } = useAuthUser();
    const userRole = useUserRole();
    const { multiCurrency } = useMultiCurrencyStorage();
    const { tenants } = useTenantsApi();

    const isOpenToggler = () => {
        setIsOpen((prev) => !prev);
    };

    const handleLogout = async () => {
        setIsOpen((prev) => !prev);
        signOut();
        navigate(ApiConfig.unAuthenticatedEntryPath, { replace: true });
    };

    const goToAccountHandler = () => {
        navigate('/account');
        isOpenToggler();
    };

    const goToNotificationsHandler = () => {
        navigate('/notifications');
        isOpenToggler();
    };

    const handleSwitchTenant = () => {
        setTenant(undefined);
        navigate('/auth/choose-hotel');
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={isOpenToggler}>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                    <UserIcon />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                className={'w-60 p-0 rounded-2.5xl border border-popover-100 bg-popover-10'}
            >
                <div className={'relative px-4 py-8 flex flex-col items-center gap-2 text-center'}>
                    <EAvatar
                        className="size-11"
                        url={
                            user?.avatarUrl
                                ? `${ApiConfig.currentUrls.api.slice(0, -1)}${user?.avatarUrl}`
                                : undefined
                        }
                    />
                    <div className="w-full min-w-0 self-stretch px-1">
                        <h3
                            className="text-sm font-medium select-none text-center line-clamp-2 [overflow-wrap:anywhere]"
                            title={user?.userName || undefined}
                        >
                            {user?.userName || ''}
                        </h3>
                        <p
                            className={cn(
                                'text-xs text-popover-1000 select-none text-center line-clamp-2 [overflow-wrap:anywhere] mt-0.5',
                                userRole === Roles.superAdmin && 'text-warning-800',
                            )}
                        >
                            {userRole && t(`header.account-popup.role.${userRole}`)}
                        </p>
                    </div>
                    <span className="absolute right-2 bottom-2 text-[10px] text-popover-400">
                        {`v ${AppConfig.defaults.version}`}
                    </span>
                </div>
                <div className={'border-b border-popover-100'}></div>
                <div className={'p-4 flex flex-col'}>
                    <Button
                        variant="ghost"
                        className={'p-3 justify-start gap-2.5 stroke-popover'}
                        onClick={goToAccountHandler}
                    >
                        <UserIcon />
                        {t('header.account-popup.account')}
                    </Button>
                    {isMobile && (
                        <>
                            <Button
                                variant="ghost"
                                className={'p-3 justify-start gap-2.5 stroke-popover'}
                                onClick={goToNotificationsHandler}
                            >
                                <NotificationIcon className="stroke-popover-1000" />
                                {t('header.account-popup.notifications')}
                            </Button>
                            <AccountPopupLanguage />
                            {multiCurrency && <AccountPopupCurrency />}
                        </>
                    )}
                    {tenants && tenants?.totalCount > 1 && (
                        <Button
                            variant="ghost"
                            className={'p-3 justify-start gap-2.5 stroke-popover'}
                            onClick={handleSwitchTenant}
                        >
                            <ArrowReloadHorizontalIcon />
                            {t('header.account-popup.switchTenant')}
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        className={
                            'p-3 justify-start gap-2.5 text-error-1000 stroke-error-1000 hover:bg-error-50 active:bg-error-100'
                        }
                        onClick={handleLogout}
                    >
                        <LogoutIcon />
                        {t('header.account-popup.logout')}
                    </Button>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default AccountPopup;
